import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import {
  mapToScopeCertificateForm,
  ScopeCertificateDates,
  ScopeCertificateDetails,
  ScopeCertificateForm,
  scopeCertificateFormToMap,
  StandardDetails,
  NonMandatoryCriterionDetails,
} from "../../../sc-models/scope-certificate-details-dto";
import ScopeCertificateDetailsForm from "./scope-certificate-details-form";
import ScopeCertificateDatesForm from "./scope-certificate-dates-form";
import StandardDetailsForm from "./standard-details-form";
import NonMandatoryCriterionDetailsForm from "./non-mandatory-criterion-details-form";
import { FooterContainer } from "../../../../../../shared/components/modals/custom-popup";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { useScStore } from "../../../../../../store/scStandard.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import apiService, { axiosInstance } from "../../../../../../shared/service/api-service";

const initScopeCertificateDetails: ScopeCertificateDetails = {
  scopeCertificateNo: "",
  brandName: [],
  gotsSCNo: "",
  scLegacyNo: "",
  scCBAuditorId: [],
  scNumberOfFarms: null, // Moved from StandardDetails
  isFarmSC: "", // "Y" or "N" — empty until user selects
  scCBLicensingCode: "",
  scVersionNo: "",
  scProductLimitations: "",
  scCreationTimestamp: "",
  scCbLicensingCodeDescription: "",
  ccsFlag: "",
  stdClaimsPolicy: "",
  certifiedOrganizationApprovedClaims: ""
};

const initScopeCertificateDates: ScopeCertificateDates = {
  scStatus: "",
  scStatusDescription: "",
  scSuspensionReason: "",
  scSuspensionReasonDescription: "",
  scCbAccreditationNo: "",
  scCreatedDate: new Date().toISOString().split('T')[0],
  scLastUpdatedDate: "",
  scExtendedUntilDate: "",
  scLastAuditStartDate: "",
  scValidFromDate: "",
  scValidUntilDate: "",
  scWithdrawalDate: "",
  scLastAuditDate: "",
  scStateOrProvince: "",
  scStateOrProvinceDescription: "",
  scPlaceOfIssue: "",
  scPlaceOfIssueDescription: "",
  scCbAccreditationBody: "",
  scCbAccreditationBodyDescription: "",
  scCbScopeStipulations: "",
};

const initScopeCertificateForm: ScopeCertificateForm = {
  scopeCertificateDetails: initScopeCertificateDetails,
  standardDetails: [],
  scopeCertificateDates: initScopeCertificateDates,
  scNonMandatoryCriterionInformation: [],
};

interface ScopeCertificateComponentProps {
  onNextTab: () => Promise<void>;
  isLocked?: boolean;
}

const ScopeCertificateComponent: React.FC<ScopeCertificateComponentProps> = ({ onNextTab, isLocked }) => {

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [data, setData] = useState<ScopeCertificateForm[]>([]);
  const [scopeCertificateData, setScopeCertificateData] =
    useState<ScopeCertificateForm>(initScopeCertificateForm);
  const { executeGet, executePost, loading } = useApi();
  const scopeDetailsRef = useRef<any>(null);
  const standardDetailsRef = useRef<any>(null);
  const certificateDatesRef = useRef<any>(null);
  const nonMandatoryCriterionDetailsRef = useRef<any>(null);
  const { isRestrictPopUpSave, setRestrictPopUpSave, cbLicensingCode } = useTransactionCertificateStore();
  const navigate = useNavigate();
  const { notify } = useNotification()
  const { formtype, SC_ID } = useParams();
  const { editSCInfo, setScStandards, setScStandardDescriptions, setScLastAuditStartDate, setScLastAuditDate, setScData, clearScData, setHasPR0035: setHasPR0035Store, incrementScDetailsSaveTick, facilityListCache } = useScStore();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");
  const [existingSCData, setExistingSCData] = useState<any>(null);

  const { setEditSCInfo } = useScStore();

  const [hasPR0035, setHasPR0035] = useState<boolean>(false);
  const [hasFarmProcessCodes, setHasFarmProcessCodes] = useState<boolean>(false);
  const [hasPR0037, setHasPR0037] = useState<boolean>(false);

  // Suspended SCs: only scStatus can change; all other fields are read-only
  const isSuspended = scopeCertificateData.scopeCertificateDates.scStatus === 'S';

  const [facilityMinAuditDate, setFacilityMinAuditDate] = useState<string>("");
  const [facilityMaxAuditDate, setFacilityMaxAuditDate] = useState<string>("");
  const [isFarmSC, setIsFarmSC] = useState<boolean>(false);

  // Stays true across BOTH executeGet calls inside fetchScopeCerificateDetails.
  // Prevents the blank shell flash that occurred when `loading` (single-request)
  // briefly went false between the two sequential API calls.
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(Boolean(SC_ID));

  const fetchScopeCerificateDetails = async () => {
    const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
    const data = mapToScopeCertificateForm(result?.data);
    const standards = result?.data?.scStandardInformation?.map(
      (item: any) => item.scStandard
    ) || [];
    setScStandards(standards);
    // Build code→description map for the detail panel to use
    const descMap: Record<string, string> = {};
    (result?.data?.scStandardInformation || []).forEach((item: any) => {
      if (item.scStandard && item.scStandardDescription) {
        descMap[item.scStandard] = item.scStandardDescription;
      }
    });
    setScStandardDescriptions(descMap);
    setScopeCertificateData(data);
    setExistingSCData(data);
    // Cache the raw API response in Zustand so other tabs (facility-details,
    // component-claims) can read standards/isFarmSC without a redundant fetch.
    setScData(result?.data);
    // Restore isFarmSC local state from the fetched value so the radio re-selects correctly
    if (data.scopeCertificateDetails.isFarmSC) {
      setIsFarmSC(data.scopeCertificateDetails.isFarmSC === 'Y');
    }

    // Fetch facility process codes for cross-tab validation (caching only)
    try {
      const facilities = useScStore.getState().facilityListCache;
      if (!facilities) {
        const facilityResponse = await executeGet(API_ROUTES.GET_FACILITY_LIST_DETAILS_V4, { scId: SC_ID });
        // GET_FACILITY_LIST_DETAILS_V4 wraps data as: { response: { facilityDetails: [...] } }
        useScStore.getState().setFacilityListCache(facilityResponse?.data?.response);
      }
    } catch (error) {
      console.error("Error fetching facility details for validation:", error);
    }

    // ── BACKGROUND PRE-FETCH ──────────────────────────────────────────────────
    // Fire and forget all static reference data used by the stepper dialogs.
    // Because these route through the dtrackit.service session cache, by the time
    // the user navigates through the stepper, the data will be instantly available.
    Promise.all([
      dtrackItServiceV4.getAccreditationBody(),
      dtrackItServiceV4.getSCStatusV4(),
      dtrackItServiceV4.getSuspensionReason(),
      dtrackItServiceV4.getScStandardValues(),
      dtrackItServiceV4.getStandardWithdrawalReasonValues(),
      dtrackItServiceV4.getNonMandatoryCriterionStandardValues(),
      dtrackItServiceV4.getNonMandatoryCriterionNumbers(),
      dtrackItServiceV4.getNonMandatoryCriterionStatusValues(),
    ]).catch(err => console.warn("[Background Pre-fetch] Failed to pre-warm stepper reference data", err));
  };
  // Reset any stale TC restriction flag so the Next button is never blocked on SC form load
  useEffect(() => {
    setRestrictPopUpSave(false);
  }, []);

  // Sync SC audit dates to the Zustand store so the Facility Last Audit Date picker
  // can constrain its range to [scLastAuditStartDate, scLastAuditDate] without prop drilling.
  useEffect(() => {
    const { scLastAuditStartDate, scLastAuditDate } = scopeCertificateData.scopeCertificateDates;
    setScLastAuditStartDate(scLastAuditStartDate ? String(scLastAuditStartDate) : '');
    setScLastAuditDate(scLastAuditDate ? String(scLastAuditDate) : '');
  }, [
    scopeCertificateData.scopeCertificateDates.scLastAuditStartDate,
    scopeCertificateData.scopeCertificateDates.scLastAuditDate,
  ]);

  useEffect(() => {
    if (SC_ID) {
      setIsInitialLoading(true);
      fetchScopeCerificateDetails().finally(() => setIsInitialLoading(false));
    }
  }, []);

  // Reactively parse facility process codes and audit dates whenever facilityListCache changes
  useEffect(() => {
    if (!facilityListCache) {
      setHasPR0035(false);
      setHasPR0035Store(false);
      setHasPR0037(false);
      setHasFarmProcessCodes(false);
      setFacilityMinAuditDate("");
      setFacilityMaxAuditDate("");
      return;
    }

    // facilityListCache can be { response: { facilityDetails: [...] } } or { facilityDetails: [...] } or an array of facilities.
    const cache = facilityListCache as any;
    let facilities: any[] = [];
    if (Array.isArray(cache)) {
      facilities = cache;
    } else if (cache.facilityDetails && Array.isArray(cache.facilityDetails)) {
      facilities = cache.facilityDetails;
    } else if (cache.response?.facilityDetails && Array.isArray(cache.response.facilityDetails)) {
      facilities = cache.response.facilityDetails;
    }

    let pr0035Found = false;
    let farmProcessFound = false;
    let pr0037Found = false;

    const INVALID_AUDIT_DATE = '1900-01-01';
    const auditDates: string[] = [];

    facilities.forEach((facility: any) => {
      // Collect valid facilityLastAuditDate values (ignore 1900-01-01 = not yet audited)
      if (facility.facilityLastAuditDate && facility.facilityLastAuditDate !== INVALID_AUDIT_DATE) {
        auditDates.push(facility.facilityLastAuditDate);
      }

      // Check process codes
      const processList = facility.facilityProcessDetails || facility.facilityProcessInformation || [];
      if (Array.isArray(processList) && processList.length > 0) {
        const facilityProcessCodes = processList.map((p: any) => p.facilityProcessCategoryCode);
        if (facilityProcessCodes.includes("PR0035")) pr0035Found = true;
        if (facilityProcessCodes.includes("PR0037")) pr0037Found = true;

        const farmCodes = ["PR0011", "PR0036", "PR0038"];
        const comboCodes = ["PR0002", "PR0014", "PR0026"];

        const hasFarm = farmCodes.some(c => facilityProcessCodes.includes(c));
        const hasCombo = comboCodes.some(c => facilityProcessCodes.includes(c));
        const hasOther = facilityProcessCodes.some((c: string) => !farmCodes.includes(c) && !comboCodes.includes(c) && c !== "PR0037");

        if (hasFarm) {
          if (hasCombo || hasOther || facilityProcessCodes.every((c: string) => farmCodes.includes(c))) {
            farmProcessFound = true;
          }
        }
      }
    });

    // Compute min/max audit dates
    if (auditDates.length > 0) {
      const sorted = [...auditDates].sort();
      setFacilityMinAuditDate(sorted[0]);
      setFacilityMaxAuditDate(sorted[sorted.length - 1]);
    } else {
      setFacilityMinAuditDate("");
      setFacilityMaxAuditDate("");
    }

    setHasPR0035(pr0035Found);
    setHasPR0035Store(pr0035Found); // share with other tabs via Zustand
    setHasPR0037(pr0037Found);
    setHasFarmProcessCodes(farmProcessFound && !pr0037Found);
  }, [facilityListCache, setHasPR0035Store]);

  // ── Auto-highlight PR0035 fields after redirect from Submit ──────────────────
  // When the Component & Claims tab navigates here after a 422 or pre-validation
  // failure, backendValidationErrors['scope-certificate-details'] will contain the
  // offending field names. We jump to step 0 and touch those fields so Formik
  // renders the red border + error message without extra user interaction.
  useEffect(() => {
    const errors = useScStore.getState().backendValidationErrors['scope-certificate-details'];
    if (!errors || errors.length === 0) return;

    const errorsLower = errors.map((e: string) => e.toLowerCase());
    const hasBrandNameError = errorsLower.some((e: string) =>
      e.startsWith('brandname') || e.includes('brand_name') || e.startsWith('brandname:') || e.includes('brandname:')
    );
    const hasApprovedClaimsError = errorsLower.some((e: string) =>
      e.startsWith('certifiedorganizationapprovedclaims') ||
      e.includes('approved_claims') ||
      e.includes('approved claims') ||
      e.startsWith('certifiedorganizationapprovedclaims:')
    );

    if (hasBrandNameError || hasApprovedClaimsError) {
      // Jump to step 0 where brandName and certifiedOrganizationApprovedClaims live
      setActiveStep(0);
      // Touch fields after a short delay to allow the form to mount/reinitialize
      setTimeout(() => {
        if (scopeDetailsRef.current) {
          if (hasBrandNameError) {
            scopeDetailsRef.current.setFieldTouched('brandName', true, false);
          }
          if (hasApprovedClaimsError) {
            scopeDetailsRef.current.setFieldTouched('certifiedOrganizationApprovedClaims', true, false);
          }
        }
      }, 300);
    }
  }, []);

  // M5: Auto-derive scStatus from dates for new SC (Create workflow only)
  // Rule: validFromDate > today → 'I' (Issued); today in [validFrom, validUntil/extendedUntil] → 'V' (Valid); past end → 'E' (Expired)
  useEffect(() => {
    if (SC_ID) return; // Existing SCs use M3 transition machine — do not override

    const { scValidFromDate, scValidUntilDate, scExtendedUntilDate } =
      scopeCertificateData.scopeCertificateDates;

    if (!scValidFromDate) return; // Dates not yet filled — nothing to derive

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const validFrom = new Date(scValidFromDate);
    const validUntil = scValidUntilDate ? new Date(scValidUntilDate) : null;
    const extendedUntil = scExtendedUntilDate ? new Date(scExtendedUntilDate) : null;

    let derivedStatus: string;
    if (validFrom > today) {
      derivedStatus = 'I'; // SC has not started yet → Issued
    } else {
      // SC has started — check expiry (prefer extendedUntil over validUntil if present)
      const effectiveEndDate = extendedUntil ?? validUntil;
      derivedStatus = effectiveEndDate && today > effectiveEndDate ? 'E' : 'V';
    }

    // Only update when status has actually changed to prevent infinite re-render
    if (derivedStatus !== scopeCertificateData.scopeCertificateDates.scStatus) {
      handleDataUpdate('scopeCertificateDates', {
        ...scopeCertificateData.scopeCertificateDates,
        scStatus: derivedStatus,
      });
    }
  }, [
    scopeCertificateData.scopeCertificateDates.scValidFromDate,
    scopeCertificateData.scopeCertificateDates.scValidUntilDate,
    scopeCertificateData.scopeCertificateDates.scExtendedUntilDate,
    scopeCertificateData.scopeCertificateDates.scStatus,
    SC_ID
  ]);


  const toggleDrawer = () => {
    setActiveStep(0);
    setDrawerOpen((prev) => !prev);
  };

  const handleDataUpdate = (stepKey: string, updatedData: any) => {
    setScopeCertificateData((prev) => ({
      ...prev,
      [stepKey]: updatedData,
    }));
  };
  // formRefs order MUST match the steps array order:
  // [0] Scope Certificate Info, [1] Certificate Dates & Accreditation, [2] Standard Info (table – null), [3] Non-mandatory Criterion (table – null)
  const formRefs = [scopeDetailsRef, certificateDatesRef, standardDetailsRef, nonMandatoryCriterionDetailsRef]
  /**
   * Validates that the YYMM portion of the SC Number matches the year+month
   * of the SC Valid From Date. Returns an error message string, or null if valid.
   *
   * This is a cross-step check (SC Number = step 1, Valid From Date = step 2),
   * so it lives in navigation logic rather than in either step's Yup schema.
   */
  const checkYymmConsistency = (scNo?: string, validFromDate?: string | number | null): string | null => {
    if (!scNo || !validFromDate) return null; // Either not yet filled — skip
    const match = scNo.match(/^SC-\d{8}-(\d{2})(\d{2})-\d{2}$/);
    if (!match) return null; // Format is wrong, Yup handles that separately
    const scYY = match[1];
    const scMM = match[2];
    const fromDate = new Date(String(validFromDate));
    if (isNaN(fromDate.getTime())) return null; // Invalid date — Yup handles that
    const fromYY = String(fromDate.getFullYear()).slice(-2);
    const fromMM = String(fromDate.getMonth() + 1).padStart(2, '0');
    if (scYY !== fromYY || scMM !== fromMM) {
      return `YYMM in SC Number (${scYY}${scMM}) must match the Year and Month of SC Valid From Date (${fromYY}${fromMM}). Please go back to Step 1 and update the SC Number.`;
    }
    return null;
  };

  const handleNext = async () => {


    if (activeStep === 3) {
      // Step 3: Non-mandatory Criterion Details (table-based, no form ref)
      if (scopeCertificateData.scNonMandatoryCriterionInformation.length > 0) {
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        notify('Please add at least one Non-mandatory Criterion Detail', 'error');
        return;
      }
    } else if (activeStep === 2) {
      // Step 2: Standard Information (table-based, no form ref)
      if (scopeCertificateData.standardDetails.length > 0) {
        // When status is Withdrawn (W), every standard row must have:
        //   • SC Standard Removed Date
        //   • SC Standard Withdrawal Reason (at least one selected)
        const currentStatus = scopeCertificateData.scopeCertificateDates.scStatus;
        if (currentStatus === 'W') {
          const incompleteRows = scopeCertificateData.standardDetails.filter((std: any) =>
            !std.scStandardRemovedDate ||
            !Array.isArray(std.scStandardWithdrawalReason) ||
            std.scStandardWithdrawalReason.length === 0
          );
          if (incompleteRows.length > 0) {
            notify(
              'SC Status is Withdrawn — all Standard rows must have a Removed Date and at least one Withdrawal Reason before proceeding.',
              'error'
            );
            return;
          }
        }
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        notify('Please add at least one Standard Detail', 'error');
        return;
      }
    } else if (activeStep === 1 && certificateDatesRef?.current?.values?.scExtendedUntilDate) {
      // Step 1: Certificate Dates — warn if extended date is set
      setDialogOpen(true);
      setPopUpTitle("Warning");
      setPopUpMessage("SC extension requires Textile Exchange approval. Proceeding assumes that approval is obtained.");
      setPopUpBtnName("Confirm");
      return;
    } else if (formRefs[activeStep]?.current) {
      // Steps with Formik forms (0: SC Info, 1: Certificate Dates)
      const formik = formRefs[activeStep].current;

      // submitForm() always increments Formik's submitCount even when validation fails.
      // This is the trigger that makes hasError=true for all invalid fields (red highlight + error text).
      await formik.submitForm();

      // Now check errors to decide whether to proceed
      const errors = await formik.validateForm();
      if (Object.keys(errors).length === 0) {

        // ── Cross-validation: BrandName required when PR0035 is present ──────────
        // Formik caches the validationSchema reference internally. When hasPR0035
        // flips true after the async facility fetch, Formik still runs the original
        // schema (hasPR0035=false), so the Yup brandName rule never fires.
        // We guard explicitly here using the live React state value instead.
        if (activeStep === 0 && hasPR0035) {
          const brandNames: string[] = formik.values?.brandName || [];
          if (brandNames.length === 0) {
            // Touch the field so FormBuilderV4 shows the red error border
            formik.setFieldTouched('brandName', true, false);
            notify('Brand Name is required when process code PR0035 is present', 'error');
            return;
          }

          const approvedClaims = formik.values?.certifiedOrganizationApprovedClaims;
          if (!approvedClaims || (typeof approvedClaims === 'string' && (approvedClaims.trim() === '' || approvedClaims.trim().toUpperCase() === 'NONE'))) {
            formik.setFieldTouched('certifiedOrganizationApprovedClaims', true, false);
            notify('Certified Organization Approved Claims is required and cannot be "NONE" when process code PR0035 is present', 'error');
            return;
          }
        }

        // Cross-step YYMM check — runs only when leaving Step 1 (Certificate Dates)
        // because that is the FIRST moment when both SC Number (step 0) AND
        // Valid From Date (step 1) are definitively known.
        // Kept out of Yup to avoid false red-field errors when navigating Back.
        if (activeStep === 1) {
          const yymmError = checkYymmConsistency(
            scopeCertificateData.scopeCertificateDetails?.scopeCertificateNo,
            formik.values?.scValidFromDate
          );
          if (yymmError) {
            notify(yymmError, 'error');
            return;
          }
        }
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      } else {
        notify('Please fill in all required fields before proceeding', 'error');
      }
    }
  };
  const handleFinish = async () => {

    if (activeStep === 3) {
      // For Non-mandatory Criterion Details tab (table-based, no form ref)
      if (scopeCertificateData.scNonMandatoryCriterionInformation.length > 0) {
        // ── PR0035 cross-validation before saving ─────────────────────────────
        // Run this regardless of which step is finishing so the data is never
        // saved to the backend with required fields missing.
        if (hasPR0035) {
          // brandName lives on step 0 — read it from scopeDetailsRef if available,
          // otherwise fall back to the cached scopeCertificateData.
          const brandNamesVal: string[] =
            scopeDetailsRef.current?.values?.brandName ??
            scopeCertificateData.scopeCertificateDetails?.brandName ??
            [];
          if (brandNamesVal.length === 0) {
            // Jump back to step 0 and highlight the field
            setActiveStep(0);
            setTimeout(() => {
              scopeDetailsRef.current?.setFieldTouched('brandName', true, false);
            }, 100);
            notify('Brand Name is required when process code PR0035 is present', 'error');
            return;
          }
          const approvedClaimsVal =
            scopeDetailsRef.current?.values?.certifiedOrganizationApprovedClaims ??
            scopeCertificateData.scopeCertificateDetails?.certifiedOrganizationApprovedClaims;
          if (!approvedClaimsVal || (typeof approvedClaimsVal === 'string' && (approvedClaimsVal.trim() === '' || approvedClaimsVal.trim().toUpperCase() === 'NONE'))) {
            setActiveStep(0);
            setTimeout(() => {
              scopeDetailsRef.current?.setFieldTouched('certifiedOrganizationApprovedClaims', true, false);
            }, 100);
            notify('Certified Organization Approved Claims is required and cannot be "NONE" when process code PR0035 is present', 'error');
            return;
          }
        }
        // Call the post function with updated data
        await postScopeCertificateDetails(scopeCertificateData);
      } else {
        notify('Please add at least one Non-mandatory Criterion Detail', 'error');
      }
    } else if (formRefs[activeStep]?.current) {
      // For other tabs with form refs
      try {
        const formik = formRefs[activeStep].current;

        // submitForm() always increments Formik's submitCount even when validation fails.
        // This is the trigger that makes hasError=true for all invalid fields (red highlight + error text).
        await formik.submitForm();

        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
          // Call the post function with updated data
          await postScopeCertificateDetails(scopeCertificateData);
        } else {
          notify('Please fill in all required fields before proceeding', 'error');
        }
      } catch (error) {
        console.error("[handleFinish] Error during form submission or validation:", error);
        notify('An error occurred during form submission', 'error');
      }
    }
  };


  const postScopeCertificateDetails = async (updatedScopeCertificateData: ScopeCertificateForm) => {
    try {
      // Validate scNumberOfFarms: mandatory whenever isFarmSC=Y, regardless of standards.
      // Value must be >= 1 (0 and negative are invalid farm counts).
      const isFarmSCValue = updatedScopeCertificateData.scopeCertificateDetails?.isFarmSC === 'Y';
      if (isFarmSCValue) {
        const scNumberOfFarms = updatedScopeCertificateData.scopeCertificateDetails?.scNumberOfFarms;
        if (scNumberOfFarms === null || scNumberOfFarms === undefined || scNumberOfFarms < 1) {
          notify('SC Number of Farms is required and must be greater than 0 when the Scope Certificate is for a Farm', 'error');
          return;
        }
      }

      // Enforce that Farm standards must be selected if it's a Farm SC
      if (updatedScopeCertificateData.scopeCertificateDetails?.isFarmSC === 'Y') {
        // Derive selectedStandards locally here (not shared with the block above)
        const selectedStandards: string[] = updatedScopeCertificateData.standardDetails?.map(
          (detail: any) => detail.scStandard as string
        ) || [];
        const requiredFarmStandards = ['RAS', 'RWS', 'RMS', 'RDS'];
        const hasRequiredFarmStandard = selectedStandards.some((standard: string) => requiredFarmStandards.includes(standard));
        if (!hasRequiredFarmStandard) {
          notify('At least one Farm Standard (RAS, RWS, RMS, RDS) must be selected when the Scope Certificate is for a Farm', 'error');
          setActiveStep(2); // Redirect to the Standard Information tab
          return;
        }
      }

      // Derive scStatus on submission as per Chaitanya's UI specs:
      // - If today > scValidUntilDate or scExtendedUntilDate → scStatus = "Expired"
      // - If scValidFromDate ≤ today  ≤ scValidUntilDate or scExtendedUntilDate → scStatus = "Valid"
      // - If scCreatedDate ≤ today < scValidFromDate → scStatus = "Issued"
      const scDates = updatedScopeCertificateData.scopeCertificateDates;
      if (scDates && scDates.scValidFromDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const validFrom = new Date(scDates.scValidFromDate);
        validFrom.setHours(0, 0, 0, 0);
        
        const validUntil = scDates.scValidUntilDate ? new Date(scDates.scValidUntilDate) : null;
        if (validUntil) validUntil.setHours(0, 0, 0, 0);
        
        const extendedUntil = scDates.scExtendedUntilDate ? new Date(scDates.scExtendedUntilDate) : null;
        if (extendedUntil) extendedUntil.setHours(0, 0, 0, 0);
        
        const effectiveEndDate = extendedUntil ?? validUntil;
        
        let derivedStatus = scDates.scStatus;
        if (validFrom > today) {
          derivedStatus = "I";
        } else if (effectiveEndDate && today > effectiveEndDate) {
          derivedStatus = "E";
        } else {
          derivedStatus = "V";
        }
        
        scDates.scStatus = derivedStatus;
        const statusDescriptions: Record<string, string> = {
          I: "Issued",
          V: "Valid",
          E: "Expired",
          W: "Withdrawn",
          S: "Suspended"
        };
        scDates.scStatusDescription = statusDescriptions[derivedStatus] || derivedStatus;
      }

      const payload = scopeCertificateFormToMap(updatedScopeCertificateData, SC_ID, cbLicensingCode);

      const queryParams = new URLSearchParams();
      // shortCode: e.g. "CUC" derived from cbLicensingCode "CB-CUC"
      const shortCode = cbLicensingCode?.replace(/^CB-/, "") || "";

      if (SC_ID) {
        queryParams.set("isNewlyCreated", "No");
        if (updatedScopeCertificateData?.scopeCertificateDetails?.scopeCertificateNo != existingSCData?.scopeCertificateDetails?.scopeCertificateNo || updatedScopeCertificateData?.scopeCertificateDetails?.scVersionNo != existingSCData?.scopeCertificateDetails?.scVersionNo) {
          // oldScNo must carry the full format (e.g. "CUC-SC-00675956-2605-00"),
          // same as payload.scNo built in scopeCertificateFormToMap.
          // existingSCData stores the part AFTER the prefix (e.g. "SC-00675956-2605-00").
          queryParams.set("oldScNo", `${shortCode}-${existingSCData.scopeCertificateDetails.scopeCertificateNo}`);
          queryParams.set("oldScVersionNo", `${shortCode}-${existingSCData.scopeCertificateDetails.scVersionNo}`);
        }
      }
      else {
        queryParams.set("isNewlyCreated", "Yes");
      }
      const finalUrl = `${API_ROUTES.POST_SC_DETAILS_V4}?${queryParams}`;
      const res = await executePost(finalUrl, payload);

      if (res?.status === 200) {
        Utills.cl(res.data.scId);
        notify("Saved Succesfully", "success");
        // Invalidate the cached SC data so downstream tabs (Facility, Component-Claims)
        // re-fetch fresh data on their next load rather than reading stale values.
        clearScData();

        // ── Fetch facility details for ALL facilities in this SC ──────────────
        // Calls GET_FACILITY_DETAILS_V4 per facility and seeds L1 Zustand cache.
        // Uses apiService directly so calls run in true parallel without
        // useApi loading-state interference or malformed URL concatenation.
        const scIdForFetch = SC_ID || res.data.scId;
        try {
          // Step 1: get facility list to collect all facilityIds for this SC
          const listResp = await apiService.get<any>(API_ROUTES.GET_FACILITY_LIST_DETAILS_V4, { scId: scIdForFetch });
          const raw = listResp?.data?.response ?? listResp?.data ?? [];
          const rawFacilities: any[] = Array.isArray(raw) ? raw : (raw?.facilityDetails || []);
          console.log('[SC Save] facility list count:', rawFacilities.length, rawFacilities.map((f: any) => f?.facilityInfo?.facilityId ?? f?.facilityId));

          if (rawFacilities.length > 0) {
            const store = useScStore.getState();

            // Step 2: call GET_FACILITY_DETAILS_V4 for each facility in parallel
            await Promise.all(
              rawFacilities.map(async (f: any) => {
                const fid = f?.facilityInfo?.facilityId ?? f?.facilityInfo?.facilityTeId ?? f?.facilityId ?? f?.facilityTeId;
                if (!fid) {
                  console.warn('[SC Save] facility has no ID:', f);
                  return;
                }
                try {
                  console.log('[SC Save] fetching facility detail for ID:', fid);
                  const detailResp = await apiService.get<any>(API_ROUTES.GET_FACILITY_DETAILS_V4, {
                    scId: scIdForFetch,
                    facilityId: fid,
                  });
                  const detail = detailResp?.data;
                  if (detail) {
                    store.setCachedFacilityDetail(fid, detail);
                    const teId = detail.facilityTeId;
                    if (teId) store.setCachedFacilityDetail(teId, detail);
                    console.log('[SC Save] cached facility', fid, 'standards:', detail.facilityStandard);
                  }
                } catch (err) {
                  console.error('[SC Save] detail fetch failed for', fid, err);
                }
              })
            );
          }
        } catch (err) {
          console.error('[SC Save] Failed to pre-fetch facility details:', err);
        }

        // Signal the Facility Details tab that SC was saved
        incrementScDetailsSaveTick();
        await onNextTab();
        navigate(
          `/v4/sc/${formtype}/certified-organization-details/${res.data.scId}`
        );
      } else {
        notify(res?.data?.Failed, 'error');
      }
    } catch (error: any) {
      console.error("Error posting scope certificate details:", error);

      // Handle 409 Conflict error - Data already exists
      if (error?.response?.status === 409) {
        notify('Data Already exists in system', 'error');
        // Set active step to 1 (second tab)
        setActiveStep(0);
      } else {
        notify('An error occurred while saving the certificate details', 'error');
      }
    }
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));


  const steps = [
    {
      label: "Scope Certificate Information",
      content: (
        <ScopeCertificateDetailsForm
          formRef={scopeDetailsRef}
          detailsData={scopeCertificateData.scopeCertificateDetails}
          setDetailsData={(data) =>
            handleDataUpdate("scopeCertificateDetails", data)
          }
          SC_ID={SC_ID}
          isAmendment={editSCInfo?.isParentStatus === true && formtype !== 'create-sc'}
          scValidFromDate={scopeCertificateData.scopeCertificateDates.scValidFromDate}
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
          hasPR0035={hasPR0035}
          hasFarmProcessCodes={hasFarmProcessCodes}
          isSuspended={isSuspended}
          isFarmSC={isFarmSC}
          setIsFarmSC={setIsFarmSC}
          isLocked={isLocked}
        />
      ),
    },
    {
      label: "Certificate Dates & Accreditation Information",
      content: (
        <ScopeCertificateDatesForm
          formRef={certificateDatesRef}
          datesData={scopeCertificateData.scopeCertificateDates}
          setDatesData={(data) =>
            handleDataUpdate("scopeCertificateDates", data)
          }
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
          isAmendment={editSCInfo?.isParentStatus === true && formtype !== 'create-sc'}
          isSuspended={isSuspended}
          facilityMinAuditDate={facilityMinAuditDate}
          facilityMaxAuditDate={facilityMaxAuditDate}
          isLocked={isLocked}
        />
      ),
    },
    {
      label: "Standard Information",
      content: (
        <StandardDetailsForm
          standardData={scopeCertificateData.standardDetails}
          setStandardData={(data) => handleDataUpdate("standardDetails", data)}
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
          ccsFlag={scopeCertificateData.scopeCertificateDetails.ccsFlag}
          scValidFromDate={scopeCertificateData.scopeCertificateDates.scValidFromDate}
          scWithdrawalDate={scopeCertificateData.scopeCertificateDates.scWithdrawalDate}
          isSuspended={isSuspended}
          isFarmSC={scopeCertificateData.scopeCertificateDetails.isFarmSC}
        />
      ),
    },
    {
      label: "Non-mandatory Criterion Information",
      content: (
        <NonMandatoryCriterionDetailsForm
          nonMandatoryCriterionData={scopeCertificateData.scNonMandatoryCriterionInformation}
          setNonMandatoryCriterionData={(data) => {
            handleDataUpdate("scNonMandatoryCriterionInformation", data);
          }}
          scStatus={scopeCertificateData.scopeCertificateDates.scStatus}
        />
      ),
    },
  ];

  // Function to navigate to next tab
  const handleNavigateToNextTab = () => {
    navigate(`/v4/sc/${formtype}/certified-organization-details/${SC_ID}`);
  };

  const actions: ActionButtonConfig[] = [

    ...(
      // activeStep === 0 &&
      editSCInfo?.isParentStatus === true && formtype !== 'create-sc' ? [
        {
          label: "Cancel Amendment",
          onClick: () => {
            setDialogOpen(true);
            setPopUpTitle("Cancel Amendment");
            setPopUpMessage("Are you sure you want to cancel this amendment?");
            setPopUpBtnName("Confirm");
          },
          variant: "contained" as const,
          color: "secondary" as const,
        }
      ] : []),
    {
      label: "Home",
      onClick: () => {
        navigate('/v4/sc-dashboard');
      },
      variant: "contained",
      color: "default",

    },
    ...(activeStep !== 0 ? [{
      label: "Back",
      onClick: handleBack,
      variant: "contained" as const,
      color: "default" as const,
      disabled: activeStep === 0,
    }] : []),
    {
      label: activeStep === steps.length - 1
        ? hasSubmitSC
          ? "Next"
          : "Save & Next"
        : "Next",
      onClick: activeStep === steps.length - 1
        ? hasSubmitSC
          ? handleNavigateToNextTab
          : handleFinish
        : handleNext,
      // Disable when: popup unsaved on step 1
      disabled: (activeStep === 1 && isRestrictPopUpSave),
      variant: "contained",
      color: "primary",
    },
  ];

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  const handleConfirm = async () => {
    if (popUpTitle === "Warning") {
      setDialogOpen(false);
      if (formRefs[activeStep]?.current) {
        const errors = await formRefs[activeStep].current.validateForm();

        await formRefs[activeStep].current.submitForm();
        if (Object.keys(errors).length === 0) {
          // Cross-step YYMM check — same gate as in handleNext for step 1
          if (activeStep === 1) {
            const yymmError = checkYymmConsistency(
              scopeCertificateData.scopeCertificateDetails?.scopeCertificateNo,
              formRefs[activeStep].current.values?.scValidFromDate
            );
            if (yymmError) {
              notify(yymmError, 'error');
              return;
            }
          }
          setActiveStep((prev) => Math.min(prev + 1, steps.length - 1)); // Move to the next step
        } else {
          notify('Please Enter the Required Values', 'error');
        }
      }
    } else {
      handleCancelAmendment();
    }

  }
  const handleCancelAmendment = async () => {
    try {
      await executePost(
        API_ROUTES.GET_SC_CANCEL_AMEND_V4,
        { scId: SC_ID, scCbLicensingCode: cbLicensingCode }
      );
      notify("Cancelled Amendment Successfully", "success");
    } catch (err) {
      console.error("Failed to cancel amendment", err);
    }
    navigate(`/v4/sc-dashboard`);
  };

  return (
    <>
      {" "}
      {isInitialLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Validation Error Banner */}
          {(() => {
            const errors = useScStore.getState().backendValidationErrors['scope-certificate-details'];
            if (!errors || errors.length === 0) return null;
            return (
              <Box mb={2} sx={{
                bgcolor: '#fff4e5',
                border: '1px solid #ff9800',
                borderRadius: '4px',
                padding: '12px 16px',
              }}>
                <Typography variant="body2" sx={{ color: '#b26a00', fontWeight: 700, mb: 0.5 }}>
                  ⚠ Validation Errors Found
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#7a4100' }}>
                  {errors.map((err, i) => (
                    <li key={i}><Typography variant="body2">{err}</Typography></li>
                  ))}
                </ul>
              </Box>
            );
          })()}

          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={(step) => setActiveStep(step)}
          />

          <div style={{ marginTop: "3rem" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              {actions.map((action, index) => (
                <StyledButton
                  key={index}
                  onClick={action.onClick}
                  variant={action.variant || "text"}
                  //   color={action.color || 'primary'}
                  disabled={action.disabled}
                >
                  {action.label}
                </StyledButton>
              ))}
            </Box>
          </div>
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleConfirm}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
        </>
      )}
    </>
  );
};

export default ScopeCertificateComponent;
